// @ts-check
import { useAuth } from 'react-oidc-context';

/**
 * Checks if the logged in user is a valid token including the required `access_token` for the api
 * @returns isAuthenticated
 */
export function useAuthenticated() {
	const { isAuthenticated, user } = useAuth();

	return isAuthenticated && Boolean(user?.access_token);
}

/**
 * @returns {boolean} isKeroOnly
 */
export function useKeroOnly() {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [], group = [] } = /** @type {*} */ (user?.profile || {});
	const hasRole = role.length > 0;
	const hasGroup = group.length > 0;

	return isAuthenticated && !hasRole && !hasGroup;
}

/**
 * @returns isBasketAdmin
 */
export function useBasketAdmin() {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { group = [] } = /** @type {*} */ (user?.profile || {});
	const hasBasketGroup =
		group instanceof Array && group.includes('basket-admin');

	return isAuthenticated && hasBasketGroup;
}

/**
 * @returns isMasquerade
 */
export function useMasquerade() {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const scopes = (user?.scope || '').split(' ');
	const isMasquerade =
		scopes.includes('masquerade') ||
		Boolean(/** @type {*} */ (user)?.profile?.masquerade);

	return isAuthenticated && isMasquerade;
}

/**
 * @returns {boolean} true if user has "AccountLogin" role
 */
export function useAccountLogin() {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [] } = /** @type {*} */ (user?.profile || {});

	return isAuthenticated && role.includes('AccountLogin');
}

/**
 * @returns {boolean} true if user has "SuspendedOwner" role
 */
export function useSuspended() {
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();
	const { role = [] } = /** @type {*} */ (user?.profile || {});

	return isAuthenticated && role.includes('SuspendedOwner');
}

export default useAuthenticated;
